import type { WebglContextSignals, WebglSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt } from '../util/safe.js';
import { hash } from '../util/hash.js';

/**
 * WebGL — самый информативный аппаратный сигнал.
 *
 * UNMASKED_RENDERER выдаёт конкретную модель GPU и графический бэкенд. Именно
 * здесь ловится большинство подмен: заявленная ОС не совпадает с семейством
 * GPU (Apple M-серия под видом Windows), Chrome под Windows без ANGLE,
 * программный рендерер там, где должно быть железо.
 */

const COMMON_PARAMS = [
  'ALIASED_LINE_WIDTH_RANGE',
  'ALIASED_POINT_SIZE_RANGE',
  'ALPHA_BITS',
  'BLUE_BITS',
  'DEPTH_BITS',
  'GREEN_BITS',
  'RED_BITS',
  'STENCIL_BITS',
  'MAX_COMBINED_TEXTURE_IMAGE_UNITS',
  'MAX_CUBE_MAP_TEXTURE_SIZE',
  'MAX_FRAGMENT_UNIFORM_VECTORS',
  'MAX_RENDERBUFFER_SIZE',
  'MAX_TEXTURE_IMAGE_UNITS',
  'MAX_TEXTURE_SIZE',
  'MAX_VARYING_VECTORS',
  'MAX_VERTEX_ATTRIBS',
  'MAX_VERTEX_TEXTURE_IMAGE_UNITS',
  'MAX_VERTEX_UNIFORM_VECTORS',
  'MAX_VIEWPORT_DIMS',
  'SUBPIXEL_BITS',
];

const WEBGL2_PARAMS = [
  'MAX_3D_TEXTURE_SIZE',
  'MAX_ARRAY_TEXTURE_LAYERS',
  'MAX_COLOR_ATTACHMENTS',
  'MAX_DRAW_BUFFERS',
  'MAX_ELEMENT_INDEX',
  'MAX_ELEMENTS_INDICES',
  'MAX_ELEMENTS_VERTICES',
  'MAX_FRAGMENT_INPUT_COMPONENTS',
  'MAX_FRAGMENT_UNIFORM_BLOCKS',
  'MAX_FRAGMENT_UNIFORM_COMPONENTS',
  'MAX_PROGRAM_TEXEL_OFFSET',
  'MAX_SAMPLES',
  'MAX_TEXTURE_LOD_BIAS',
  'MAX_TRANSFORM_FEEDBACK_INTERLEAVED_COMPONENTS',
  'MAX_TRANSFORM_FEEDBACK_SEPARATE_ATTRIBS',
  'MAX_TRANSFORM_FEEDBACK_SEPARATE_COMPONENTS',
  'MAX_UNIFORM_BLOCK_SIZE',
  'MAX_UNIFORM_BUFFER_BINDINGS',
  'MAX_VARYING_COMPONENTS',
  'MAX_VERTEX_OUTPUT_COMPONENTS',
  'MAX_VERTEX_UNIFORM_BLOCKS',
  'MAX_VERTEX_UNIFORM_COMPONENTS',
  'MIN_PROGRAM_TEXEL_OFFSET',
  'UNIFORM_BUFFER_OFFSET_ALIGNMENT',
];

const SHADER_TYPES = ['VERTEX_SHADER', 'FRAGMENT_SHADER'];
const PRECISION_TYPES = [
  'LOW_FLOAT',
  'MEDIUM_FLOAT',
  'HIGH_FLOAT',
  'LOW_INT',
  'MEDIUM_INT',
  'HIGH_INT',
];

const VERTEX_SRC = `
attribute vec2 aPos;
varying vec2 vPos;
void main() {
  vPos = aPos;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Тригонометрия и деление намеренно: компиляторы шейдеров у разных драйверов
// по-разному раскладывают эти операции, и младшие биты результата расходятся.
const FRAGMENT_SRC = `
precision mediump float;
varying vec2 vPos;
void main() {
  float d = length(vPos);
  float r = sin(vPos.x * 12.9898) * 0.5 + 0.5;
  float g = cos(vPos.y * 78.233) * 0.5 + 0.5;
  float b = fract(sin(d * 43758.5453) * 1000.0);
  gl_FragColor = vec4(r, g, b, 1.0);
}`;

function toPlain(value: unknown): unknown {
  if (ArrayBuffer.isView(value)) {
    return Array.from(value as unknown as ArrayLike<number>);
  }
  return value;
}

function readParams(gl: WebGLRenderingContext, names: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const ctx = gl as unknown as Record<string, number>;
  for (const name of names) {
    const enumValue = ctx[name];
    if (typeof enumValue !== 'number') continue;
    const value = attempt(() => gl.getParameter(enumValue));
    if (value !== null) out[name] = toPlain(value);
  }
  return out;
}

function readPrecision(gl: WebGLRenderingContext): Record<string, [number, number, number]> {
  const out: Record<string, [number, number, number]> = {};
  const ctx = gl as unknown as Record<string, number>;
  for (const shader of SHADER_TYPES) {
    for (const precision of PRECISION_TYPES) {
      const s = ctx[shader];
      const p = ctx[precision];
      if (typeof s !== 'number' || typeof p !== 'number') continue;
      const format = attempt(() => gl.getShaderPrecisionFormat(s, p));
      if (format) {
        out[`${shader}.${precision}`] = [format.rangeMin, format.rangeMax, format.precision];
      }
    }
  }
  return out;
}

function renderHash(gl: WebGLRenderingContext, width: number, height: number): string | null {
  return attempt(() => {
    const program = gl.createProgram();
    const vs = gl.createShader(gl.VERTEX_SHADER);
    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    if (!program || !vs || !fs) return null;

    gl.shaderSource(vs, VERTEX_SRC);
    gl.compileShader(vs);
    gl.shaderSource(fs, FRAGMENT_SRC);
    gl.compileShader(fs);
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const loc = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    gl.viewport(0, 0, width, height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    gl.deleteBuffer(buffer);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    gl.deleteProgram(program);

    return hash(pixels);
  });
}

function collectContext(version: 1 | 2): WebglContextSignals {
  const empty: WebglContextSignals = {
    version,
    supported: false,
    vendor: null,
    renderer: null,
    unmaskedVendor: null,
    unmaskedRenderer: null,
    glVersion: null,
    shadingLanguageVersion: null,
    extensions: null,
    parameters: null,
    shaderPrecision: null,
    contextAttributes: null,
    imageHash: null,
    repeatImageHashes: null,
    stable: null,
  };

  const width = 128;
  const height = 128;

  const gl = attempt(() => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const names = version === 2 ? ['webgl2'] : ['webgl', 'experimental-webgl'];
    for (const name of names) {
      const context = canvas.getContext(name, { preserveDrawingBuffer: true });
      if (context) return context as WebGLRenderingContext;
    }
    return null;
  });

  if (!gl) return empty;

  const debugInfo = attempt(() => gl.getExtension('WEBGL_debug_renderer_info')) as {
    UNMASKED_VENDOR_WEBGL?: number;
    UNMASKED_RENDERER_WEBGL?: number;
  } | null;

  const params = readParams(gl, version === 2 ? [...COMMON_PARAMS, ...WEBGL2_PARAMS] : COMMON_PARAMS);

  // Анизотропия живёт в расширении, но это заметный параметр железа.
  const aniso = attempt(() => {
    const ext = (gl.getExtension('EXT_texture_filter_anisotropic') ??
      gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic')) as {
      MAX_TEXTURE_MAX_ANISOTROPY_EXT: number;
    } | null;
    return ext ? gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) : null;
  });
  if (aniso !== null) params['MAX_TEXTURE_MAX_ANISOTROPY_EXT'] = aniso;

  const repeatImageHashes: string[] = [];
  for (let i = 0; i < 2; i++) {
    const h = renderHash(gl, width, height);
    if (h) repeatImageHashes.push(h);
  }

  return {
    version,
    supported: true,
    vendor: attempt(() => String(gl.getParameter(gl.VENDOR))),
    renderer: attempt(() => String(gl.getParameter(gl.RENDERER))),
    unmaskedVendor: debugInfo?.UNMASKED_VENDOR_WEBGL
      ? attempt(() => String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL!)))
      : null,
    unmaskedRenderer: debugInfo?.UNMASKED_RENDERER_WEBGL
      ? attempt(() => String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL!)))
      : null,
    glVersion: attempt(() => String(gl.getParameter(gl.VERSION))),
    shadingLanguageVersion: attempt(() => String(gl.getParameter(gl.SHADING_LANGUAGE_VERSION))),
    extensions: attempt(() => gl.getSupportedExtensions()?.slice().sort() ?? null),
    parameters: params,
    shaderPrecision: readPrecision(gl),
    contextAttributes: attempt(
      () => gl.getContextAttributes() as unknown as Record<string, unknown>,
    ),
    imageHash: repeatImageHashes[0] ?? null,
    repeatImageHashes,
    stable: repeatImageHashes.length > 1 ? new Set(repeatImageHashes).size === 1 : null,
  };
}

const GPU_FAMILIES: [RegExp, string][] = [
  [/apple\s*(m\d|gpu|a\d)/i, 'apple'],
  [/nvidia|geforce|quadro|rtx|gtx/i, 'nvidia'],
  [/amd|radeon|ati\b|rx\s*\d/i, 'amd'],
  [/intel|hd graphics|uhd graphics|iris|arc\b/i, 'intel'],
  [/adreno/i, 'adreno'],
  [/mali/i, 'mali'],
  [/powervr/i, 'powervr'],
  [/swiftshader/i, 'swiftshader'],
  [/llvmpipe|mesa offscreen|softpipe/i, 'llvmpipe'],
  [/microsoft basic render/i, 'microsoft-basic'],
  [/virgl|virtio|vmware|virtualbox|parallels|qemu/i, 'virtual'],
];

const SOFTWARE_RENDERERS =
  /swiftshader|llvmpipe|mesa offscreen|softpipe|microsoft basic render|software adapter|apple software renderer/i;

export const webglModule = defineModule({
  id: 'webgl',
  phase: 1,
  timeoutMs: 5000,
  run(): WebglSignals {
    const v1 = collectContext(1);
    const v2 = collectContext(2);

    const rendererString = [
      v2.unmaskedRenderer,
      v1.unmaskedRenderer,
      v2.renderer,
      v1.renderer,
    ]
      .filter(Boolean)
      .join(' ');

    let gpuFamily: string | null = null;
    if (rendererString) {
      gpuFamily = 'unknown';
      for (const [pattern, family] of GPU_FAMILIES) {
        if (pattern.test(rendererString)) {
          gpuFamily = family;
          break;
        }
      }
    }

    return {
      v1: v1.supported ? v1 : null,
      v2: v2.supported ? v2 : null,
      gpuFamily,
      isSoftwareRenderer: rendererString ? SOFTWARE_RENDERERS.test(rendererString) : null,
      usesAngle: rendererString ? /angle/i.test(rendererString) : null,
    };
  },
});
